<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  waitingSince: {
    type: [String, Number],
    default: null,
  },
  thresholdMinutes: {
    type: Number,
    default: 10,
  },
});

const { t } = useI18n();
const currentTime = ref(Date.now());
let intervalId = null;

onMounted(() => {
  // Update every 30 seconds for efficiency
  intervalId = setInterval(() => {
    currentTime.value = Date.now();
  }, 30000);
});

onUnmounted(() => {
  if (intervalId) {
    clearInterval(intervalId);
  }
});

const waitingMinutes = computed(() => {
  if (!props.waitingSince) return 0;

  const waitingSinceTime =
    typeof props.waitingSince === 'string'
      ? new Date(props.waitingSince).getTime()
      : props.waitingSince * 1000;

  const diffMs = currentTime.value - waitingSinceTime;
  return Math.max(0, Math.floor(diffMs / 60000));
});

const exceedsThreshold = computed(() => {
  return waitingMinutes.value >= props.thresholdMinutes;
});

const formattedTime = computed(() => {
  const mins = waitingMinutes.value;
  if (mins < 60) {
    return `${mins}m`;
  }
  const hours = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  return remainingMins > 0 ? `${hours}h ${remainingMins}m` : `${hours}h`;
});

const showBadge = computed(() => {
  return props.waitingSince && waitingMinutes.value > 0;
});
</script>

<template>
  <div
    v-if="showBadge"
    v-tooltip.left="
      t('COMPLIANCE.WAITING_TIME_TOOLTIP', { minutes: waitingMinutes })
    "
    class="flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium rounded-full transition-colors"
    :class="
      exceedsThreshold
        ? 'bg-r-50 text-r-700 dark:bg-r-900 dark:text-r-300'
        : 'bg-n-alpha-2 text-n-slate-11'
    "
  >
    <span
      v-if="exceedsThreshold"
      class="w-1.5 h-1.5 rounded-full bg-r-500 animate-pulse"
    />
    <span>{{ formattedTime }}</span>
  </div>
  <span v-else />
</template>
